import { useCallback, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import type { House, NotificationResponse } from '@/client/types.gen';
import { isHouseSetupComplete } from '@/utils/houseUtils';
import { isNotificationUnread } from '@/utils/notificationStatusUtils';
import { notificationListQueryOptions } from '@/utils/notifications/notificationQueryOptions';

type NotificationQueryResult = {
	data?: NotificationResponse[];
};

/**
 * Returns properties with unread notifications for cross-property navigation surfaces.
 *
 * Fans out one request per property when mounted, which is acceptable while accounts rarely exceed
 * ~30 properties. Only a caller that stays mounted should pass `refetchInterval`: every observer runs
 * its own timer, so two polling callers would each refresh all N. Query keys match
 * HouseNotificationsContext, allowing the selected property to reuse its cache.
 *
 * "Unread" follows the drawer's item dot and Unread filter (isNotificationUnread), not the bell
 * badge, which counts only actionable notifications.
 */
export const useUnreadNotificationPropertyIds = (
	houses: House[],
	options: { excludedHouseIds?: Set<number>; refetchInterval?: number | false } = {},
) => {
	const { excludedHouseIds, refetchInterval = false } = options;
	const houseIds = useMemo(
		() =>
			houses
				.filter((house) => isHouseSetupComplete(house) && !excludedHouseIds?.has(house.id))
				.map((house) => house.id),
		[excludedHouseIds, houses],
	);

	const combine = useCallback(
		(results: readonly NotificationQueryResult[]) => {
			const propertyIds = new Set<number>();
			for (let index = 0; index < results.length; index += 1) {
				if (results[index].data?.some(isNotificationUnread)) {
					propertyIds.add(houseIds[index]);
				}
			}
			return propertyIds;
		},
		[houseIds],
	);

	return useQueries({
		// TODO(notifications): Replace this property-level fan-out with a user notification-summary
		// endpoint (has_unread or an unread count) when the API exposes one, before portfolios outgrow the
		// fan-out. Until then, an unread item older than the newest 50 is missed here as in the drawer.
		queries: houseIds.map((houseId) => ({
			...notificationListQueryOptions(houseId),
			// Observer-level, so the drawer's 30s poll of the selected property is unaffected.
			staleTime: 5 * 60_000,
			refetchInterval,
			refetchOnWindowFocus: false,
		})),
		combine,
	});
};
